import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { parseNumericInput } from '../utils/numberFormat.js'
import { generateRequestId } from '../utils/requestId.js'

import ConnectionStatus from './ConnectionStatus.jsx'
import { t } from '../../i18n.js'
import { HotTable } from '@handsontable/react-wrapper'
import { registerAllModules } from 'handsontable/registry'
import { TextEditor } from 'handsontable/editors'
import { numericRenderer } from 'handsontable/renderers'
import 'handsontable/styles/handsontable.css'
import 'handsontable/styles/ht-theme-horizon.css'
import { cable } from '../../cable.js'
import { handleHotKeyboardShortcut } from '../utils/hotKeyboardShortcuts.js'
import { showError } from '../utils/notifications.js'
import usePasteOverflow from '../hooks/usePasteOverflow.js'
import { HOT_LICENSE_KEY } from '../utils/handsontableLicense.js'
import { hotNumericFormat } from '../utils/handsontableConfig.js'
import { sanitizeHandsontableHtml } from '../utils/handsontableSanitizer.js'

registerAllModules()

// --- Formula support: module-level refs for Handsontable class-based editor ---
let _rawDataRef = null
// Keyed by "recordId:colProp" (not row index) so entries survive rows being
// added above / refetches reordering the grid (EVA-138).
let _localFormulas = new Map()

class FormulaEditor extends TextEditor {
  prepare(row, col, prop, td, originalValue, cellProperties) {
    const colProp = COL_PROPS[col]
    const rowId = _rawDataRef?.[row]?.id
    const serverFormula = _rawDataRef?.[row]?.formulas?.[colProp]
    const localFormula = _localFormulas.get(`${rowId}:${colProp}`)
    const formula = localFormula || serverFormula
    super.prepare(row, col, prop, td, formula || originalValue, cellProperties)
  }
}

function formulaAwareRenderer(instance, td, row, col, prop, value, cellProperties) {
  numericRenderer(instance, td, row, col, prop, value, cellProperties)

  // Append a pale unit suffix (e.g. "%") when the cell has one
  if (cellProperties.unitSuffix && value !== null && value !== undefined && value !== '') {
    const suffix = document.createElement('span')
    suffix.className = 'ht-unit-suffix'
    suffix.textContent = ` ${cellProperties.unitSuffix}`
    td.appendChild(suffix)
  }

  const colProp = COL_PROPS[col]
  const rowId = _rawDataRef?.[row]?.id
  const serverFormula = _rawDataRef?.[row]?.formulas?.[colProp]
  const localFormula = _localFormulas.get(`${rowId}:${colProp}`)
  if (serverFormula || localFormula) {
    td.classList.add('ht-formula-cell')
    td.title = serverFormula || localFormula
  }
}

const DEBOUNCE_DELAY = 800

const SOURCE = {
  EDIT: 'edit',
  SERVER: 'server',
  LOAD: 'loadData'
}

// Column indices. Order follows the TI & LC dev note: after "Indexation after
// renewal" come TI (unit/growth/new/renew) then LC (unit/growth/new/renew), with
// "MLA After Expiry" last.
const COL = {
  NAME: 0,
  MARKET_RENT_UNIT: 1,
  MARKET_RENT: 2,
  MR_GROWTH_RATE: 3,
  RENEWAL_PROB: 4,
  LEASE_Y: 5,
  LEASE_M: 6,
  VOID: 7,
  RF_NEW: 8,
  RF_RENEW: 9,
  IDX_PROFILE_AFTER_RENEWAL: 10,
  TI_UNIT: 11,
  TI_GROWTH_RATE: 12,
  TI_NEW: 13,
  TI_RENEW: 14,
  LC_UNIT: 15,
  LC_GROWTH_RATE: 16,
  LC_NEW: 17,
  LC_RENEW: 18,
  AFTER_EXPIRY: 19
}

const FORMULA_ELIGIBLE_COLS = new Set([
  COL.MARKET_RENT, COL.RENEWAL_PROB, COL.LEASE_Y, COL.LEASE_M,
  COL.VOID, COL.RF_NEW, COL.RF_RENEW,
  COL.LC_NEW, COL.LC_RENEW, COL.TI_NEW, COL.TI_RENEW
])

// Data property names (matching server field names), index-aligned to COL.
const COL_PROPS = [
  'name',
  'market_rent_unit',
  'market_rent',
  'market_rent_growth_rate_id',
  'renewal_probability',
  'lease_length_years',
  'lease_length_months',
  'void_months',
  'rent_free_new_months',
  'rent_free_renew_months',
  'indexation_profile_after_renewal_id',
  'ti_unit',
  'ti_growth_rate_id',
  'ti_new',
  'ti_renew',
  'lc_unit',
  'lc_growth_rate_id',
  'lc_new',
  'lc_renew',
  'mla_after_expiry_id'
]

function buildEnumMapping(options) {
  return {
    valueToLabel: Object.fromEntries(options.map(o => [String(o.value), o.label])),
    labelToValue: Object.fromEntries(options.map(o => [o.label, o.value])),
    labels: options.map(o => o.label)
  }
}

// EVA-269: the unit options arrive from the server (`meta.*_units`, built by
// MlaEnums from I18n) so the grid, the rent-roll MLA modal and the audit trail
// cannot drift apart. They used to be a second hardcoded copy here.

export default function MlaTable({
  valuationId, indexUrl, batchUpdateUrl, batchCreateUrl, createRowUrl, destroyRowUrl,
  csrfToken, readOnly = false, translations
}) {
  const hotRef = useRef(null)
  const viewportRef = useRef(null)
  const pendingRef = useRef(new Map())
  const flushTimeoutRef = useRef(null)
  const [rawData, setRawData] = useState([])
  const [meta, setMeta] = useState({})
  const [loading, setLoading] = useState(true)
  const [hotHeight, setHotHeight] = useState(null)
  const [connectionStatus, setConnectionStatus] = useState('connecting')
  const [connectionMountEl, setConnectionMountEl] = useState(null)
  // Synchronous update — renderer reads this DURING render, not after
  _rawDataRef = rawData
  // Clear local formula indicators once server confirms (rawData updated)
  useEffect(() => { _localFormulas.clear() }, [rawData])

  // Growth rate dropdown mapping from meta
  const grMapping = useMemo(() => {
    const rates = meta.growth_rates || []
    return {
      idToLabel: Object.fromEntries(rates.map(r => [String(r.value), r.label])),
      labelToId: Object.fromEntries(rates.map(r => [r.label, r.value])),
      labels: rates.map(r => r.label)
    }
  }, [meta.growth_rates])

  // Indexation profile dropdown mapping from meta
  const profileMapping = useMemo(() => {
    const profiles = meta.indexation_profiles || []
    return {
      idToLabel: Object.fromEntries(profiles.map(p => [String(p.value), p.label])),
      labelToId: Object.fromEntries(profiles.map(p => [p.label, p.value])),
      labels: profiles.map(p => p.label)
    }
  }, [meta.indexation_profiles])

  // Unit enum mappings — labels come from the server, already carrying the
  // valuation's currency and area unit.
  const marketRentUnitMap = useMemo(
    () => buildEnumMapping(meta.market_rent_units || []),
    [meta.market_rent_units]
  )
  const lcUnitMap = useMemo(() => buildEnumMapping(meta.lc_units || []), [meta.lc_units])
  const tiUnitMap = useMemo(() => buildEnumMapping(meta.ti_units || []), [meta.ti_units])

  // MLA After Expiry mapping from rawData (for per-row filtering in cells())
  const mlaMapping = useMemo(() => {
    return {
      idToName: Object.fromEntries(rawData.filter(r => r.id && r.name).map(r => [String(r.id), r.name])),
      nameToId: Object.fromEntries(rawData.filter(r => r.id && r.name).map(r => [r.name, r.id]))
    }
  }, [rawData])

  // Mount connection badge
  useEffect(() => {
    const container = document.querySelector('[data-react-component-component-value="MlaTable"]')
    const cardHeader = container?.closest('.card')?.querySelector('.card-header')
    const titleEl = cardHeader?.querySelector('h5.card-title')
    if (!titleEl) return

    const mount = document.createElement('span')
    mount.className = 'tenants-connection-status-mount'
    titleEl.appendChild(mount)
    setConnectionMountEl(mount)

    return () => { setConnectionMountEl(null); mount.remove() }
  }, [])

  // Fetch data
  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    try {
      const res = await fetch(indexUrl, {
        headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken }
      })
      const json = await res.json()
      if (json.success) {
        setRawData(json.data || [])
        if (json.meta) setMeta(json.meta)
      }
    } catch (e) {
      console.error('Failed to fetch MLAs:', e)
    } finally {
      setLoading(false)
    }
  }, [indexUrl, csrfToken])

  useEffect(() => { fetchData() }, [fetchData])

  // EVA-138: paste overflow — create missing rows server-side, then replay.
  // Callbacks reference flushChanges/scheduledFlush lazily (defined below);
  // they only execute after the render completes, so no TDZ issue.
  const { handleBeforePaste, creatingRowsRef } = usePasteOverflow({
    hotRef,
    enabled: !readOnly,
    createRows: async (count) => {
      const res = await fetch(batchCreateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          request_id: generateRequestId(),
          items: Array.from({ length: count }, (_, i) => ({ temp_id: `paste_${Date.now()}_${i}`, data: {} }))
        })
      })
      const json = await res.json()
      return json.success === true
    },
    // Silent fetch is mandatory: a non-silent one unmounts HotTable (spinner)
    refetchData: () => fetchData(true),
    flushNow: () => {
      if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current)
      return flushChanges()
    },
    getAnchorId: (row) => _rawDataRef?.[row]?.id,
    resolveAnchorRow: (id) => {
      const idx = (_rawDataRef || []).findIndex(r => r.id === id)
      return idx >= 0 ? idx : undefined
    }
  })

  // Swarm D13: defer the full-table fetchData when a cell editor is open so
  // an unrelated channel push (MLA / GR / IndexationProfiles) doesn't swap
  // tableData mid-typing. Drained on afterDeselect / afterDestroyEditor.
  const deferredMetaRefreshRef = useRef(false)
  const requestMetaRefresh = useCallback(() => {
    // Drop pushes while a paste-create is in flight: a refetch here would race
    // the replay. Self-healing — the post-replay flush triggers an :update
    // broadcast that arrives after the guard is reset and resyncs everyone.
    if (creatingRowsRef.current) return
    const hot = hotRef.current?.hotInstance
    const editor = hot?.getActiveEditor?.()
    if (editor && typeof editor.isOpened === 'function' && editor.isOpened()) {
      deferredMetaRefreshRef.current = true
      return
    }
    fetchData(true)
  }, [fetchData, creatingRowsRef])

  useEffect(() => {
    const hot = hotRef.current?.hotInstance
    if (!hot) return undefined
    const drain = () => {
      if (!deferredMetaRefreshRef.current) return
      deferredMetaRefreshRef.current = false
      fetchData(true)
    }
    hot.addHook('afterDeselect', drain)
    hot.addHook('afterDestroyEditor', drain)
    return () => {
      hot.removeHook('afterDeselect', drain)
      hot.removeHook('afterDestroyEditor', drain)
    }
  }, [fetchData])

  // ActionCable: MLA channel + GrowthRates channel (for dropdown refresh)
  useEffect(() => {
    const mlaSub = cable.subscriptions.create(
      { channel: 'MarketLeasingAssumptionsChannel', valuation_id: valuationId },
      {
        connected() { setConnectionStatus('connected') },
        disconnected() { setConnectionStatus('disconnected') },
        received() { requestMetaRefresh() }
      }
    )
    const grSub = cable.subscriptions.create(
      { channel: 'GrowthRatesChannel', valuation_id: valuationId },
      { received() { requestMetaRefresh() } }
    )
    const profileSub = cable.subscriptions.create(
      { channel: 'IndexationProfilesChannel', valuation_id: valuationId },
      { received() { requestMetaRefresh() } }
    )
    return () => { mlaSub.unsubscribe(); grSub.unsubscribe(); profileSub.unsubscribe() }
  }, [valuationId, requestMetaRefresh])

  // Add Row handler
  useEffect(() => {
    if (readOnly) return
    const handler = async () => {
      try {
        const res = await fetch(createRowUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-Token': csrfToken
          },
          body: JSON.stringify({ request_id: generateRequestId() })
        })
        const json = await res.json()
        if (json.success) {
          await fetchData(true)
          requestAnimationFrame(() => {
            setTimeout(() => {
              const hot = hotRef.current?.hotInstance
              if (!hot) return
              const lastRow = hot.countRows() - 1
              hot.selectCell(lastRow, COL.NAME)
              hot.getActiveEditor()?.beginEditing()
            }, 50)
          })
        }
      } catch (e) {
        console.error('Failed to create MLA row:', e)
      }
    }
    window.addEventListener('addMlaRow', handler)
    return () => window.removeEventListener('addMlaRow', handler)
  }, [readOnly, createRowUrl, csrfToken, fetchData])

  // Build table data: convert server values to display values
  const tableData = useMemo(() => {
    return rawData.map(row => {
      void profileMapping // keep dep visible to lint and re-render
    return COL_PROPS.map((prop, colIdx) => {
        const val = row[prop]
        // Convert IDs/enums to labels for display
        if (colIdx === COL.MARKET_RENT_UNIT) return marketRentUnitMap.valueToLabel[val] || ''
        if (colIdx === COL.MR_GROWTH_RATE) return grMapping.idToLabel[String(val)] || ''
        if (colIdx === COL.TI_GROWTH_RATE) return grMapping.idToLabel[String(val)] || ''
        if (colIdx === COL.LC_GROWTH_RATE) return grMapping.idToLabel[String(val)] || ''
        if (colIdx === COL.LC_UNIT) return lcUnitMap.valueToLabel[val] || ''
        if (colIdx === COL.TI_UNIT) return tiUnitMap.valueToLabel[val] || ''
        if (colIdx === COL.AFTER_EXPIRY) return mlaMapping.idToName[String(val)] || ''
        if (colIdx === COL.IDX_PROFILE_AFTER_RENEWAL) return profileMapping.idToLabel[String(val)] || ''
        return val ?? ''
      })
    })
  }, [rawData, grMapping, mlaMapping, marketRentUnitMap, lcUnitMap, tiUnitMap, profileMapping])

  // Flush pending changes (pendingRef is keyed by record id, not row index,
  // so a refetch/reorder between edit and flush can't misroute the update)
  const flushChanges = useCallback(async () => {
    const pending = pendingRef.current
    if (pending.size === 0) return

    const items = []
    for (const [recordId, changedCols] of pending.entries()) {
      const changes = {}
      const formulas = {}
      let hasFormulas = false

      for (const [col, value] of changedCols.entries()) {
        if (value && typeof value === 'object' && 'formula' in value) {
          formulas[COL_PROPS[col]] = value.formula
          hasFormulas = true
        } else if (value && typeof value === 'object' && 'clearFormula' in value) {
          changes[COL_PROPS[col]] = value.value
          formulas[value.clearFormula] = null
          hasFormulas = true
        } else {
          changes[COL_PROPS[col]] = value
        }
      }

      const item = { id: recordId, changes }
      if (hasFormulas) item.formulas = formulas
      items.push(item)
    }
    pendingRef.current = new Map()

    if (items.length === 0) return

    try {
      const res = await fetch(batchUpdateUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ items, request_id: generateRequestId() })
      })
      const json = await res.json()
      if (!json.success) {
        // The whole batch rolled back server-side — the grid is now showing
        // values that don't exist in the DB. Tell the user and resync.
        showError(t('save_error_invalid', translations))
        fetchData(true)
      }
    } catch (e) {
      console.error('Failed to batch update MLAs:', e)
      showError(t('save_error', translations))
      fetchData(true)
    }
  }, [batchUpdateUrl, csrfToken, fetchData, translations])

  const scheduledFlush = useCallback(() => {
    if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current)
    flushTimeoutRef.current = setTimeout(() => flushChanges(), DEBOUNCE_DELAY)
  }, [flushChanges])

  // Flush on unmount
  useEffect(() => {
    return () => {
      if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current)
      flushChanges()
    }
  }, [flushChanges])

  // Delete row handler
  const handleDeleteRow = useCallback(async (rowIndex) => {
    const record = rawData[rowIndex]
    if (!record) return
    if (!confirm(t('delete_confirm', translations, { name: record.name }))) return

    try {
      const res = await fetch(destroyRowUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ id: record.id, request_id: generateRequestId() })
      })
      const json = await res.json()
      if (json.success) fetchData(true)
    } catch (e) {
      console.error('Failed to delete MLA row:', e)
    }
  }, [rawData, destroyRowUrl, csrfToken, fetchData, translations])

  // Convert display value back to server value for a given column
  function displayToServer(col, displayValue) {
    if (col === COL.MARKET_RENT_UNIT) return marketRentUnitMap.labelToValue[displayValue] ?? null
    if (col === COL.MR_GROWTH_RATE) return grMapping.labelToId[displayValue] ?? null
    if (col === COL.TI_GROWTH_RATE) return grMapping.labelToId[displayValue] ?? null
    if (col === COL.LC_GROWTH_RATE) return grMapping.labelToId[displayValue] ?? null
    if (col === COL.LC_UNIT) return lcUnitMap.labelToValue[displayValue] ?? null
    if (col === COL.TI_UNIT) return tiUnitMap.labelToValue[displayValue] ?? null
    if (col === COL.AFTER_EXPIRY) return mlaMapping.nameToId[displayValue] ?? null
    if (col === COL.IDX_PROFILE_AFTER_RENEWAL) return profileMapping.labelToId[displayValue] ?? null
    // Numeric columns: parse
    if ([COL.MARKET_RENT, COL.RENEWAL_PROB, COL.LEASE_Y, COL.LEASE_M, COL.VOID,
         COL.RF_NEW, COL.RF_RENEW, COL.LC_NEW, COL.LC_RENEW, COL.TI_NEW, COL.TI_RENEW].includes(col)) {
      if (displayValue === '' || displayValue === null || displayValue === undefined) return null
      const n = parseNumericInput(displayValue)
      return Number.isFinite(n) ? n : null
    }
    return displayValue
  }

  // Determine numeric format based on unit for LC/TI value columns
  function getUnitDependentFormat(row, col) {
    const record = rawData[row]
    if (!record) return { hotFormat: hotNumericFormat(2, true) }

    let unit = null
    if (col === COL.LC_NEW || col === COL.LC_RENEW) unit = record.lc_unit
    if (col === COL.TI_NEW || col === COL.TI_RENEW) unit = record.ti_unit

    if (unit === 'PERCENT_MARKET_RENT' || unit === 'PERCENT_ECONOMIC_RENT' ||
        unit === 'PERCENT_RENT_AFTER_RF_FULL_TERM' || unit === 'PERCENT_RENT_YEAR_1') {
      return { hotFormat: hotNumericFormat(2, false), suffix: '%' }
    }
    if (unit === 'MONTHS_RENT') {
      return { hotFormat: hotNumericFormat(0, false) }
    }
    if (unit === 'NONE') {
      return { hotFormat: hotNumericFormat(2, true), readOnly: true }
    }
    return { hotFormat: hotNumericFormat(2, true) }
  }

  const nestedHeaders = useMemo(() => [
    [
      { label: t('groups.general', translations), colspan: 4 },
      { label: t('groups.renewal_assumptions', translations), colspan: 6 },
      { label: t('groups.indexation_profile', translations), colspan: 1 },
      { label: t('groups.tenant_improvements', translations), colspan: 4 },
      { label: t('groups.leasing_commission', translations), colspan: 4 },
      { label: t('groups.after_expiry', translations), colspan: 1 }
    ],
    [
      t('columns.name', translations), t('columns.mr_unit', translations), t('columns.market_rent', translations), t('columns.mr_growth_rate', translations),
      t('columns.renewal_percent', translations), t('columns.lease_years', translations), t('columns.lease_months', translations), t('columns.void', translations), t('columns.rf_new', translations), t('columns.rf_renew', translations),
      t('columns.indexation_profile_renewal', translations),
      t('columns.ti_unit', translations), t('columns.ti_growth_rate', translations), t('columns.ti_new', translations), t('columns.ti_renew', translations),
      t('columns.lc_unit', translations), t('columns.lc_growth_rate', translations), t('columns.lc_new', translations), t('columns.lc_renew', translations),
      t('columns.after_expiry', translations)
    ]
  ], [translations])

  const columns = useMemo(() => [
    // 0: Name
    { data: 0, type: 'text', width: 150 },
    // 1: Market Rent Unit
    { data: 1, type: 'dropdown', source: marketRentUnitMap.labels, width: 130 },
    // 2: Market Rent
    { data: 2, type: 'numeric', ...hotNumericFormat(2, true), width: 100 },
    // 3: MR Growth Rate
    { data: 3, type: 'dropdown', source: grMapping.labels, width: 140 },
    // 4: Renewal Probability
    { data: 4, type: 'numeric', ...hotNumericFormat(0, false), width: 90 },
    // 5: Lease Length Years
    { data: 5, type: 'numeric', ...hotNumericFormat(0, false), width: 70 },
    // 6: Lease Length Months
    { data: 6, type: 'numeric', ...hotNumericFormat(0, false), width: 70 },
    // 7: Void Months
    { data: 7, type: 'numeric', ...hotNumericFormat(0, false), width: 60 },
    // 8: Rent Free New
    { data: 8, type: 'numeric', ...hotNumericFormat(0, false), width: 70 },
    // 9: Rent Free Renew
    { data: 9, type: 'numeric', ...hotNumericFormat(0, false), width: 80 },
    // 10: Indexation Profile (After Renewal)
    { data: 10, type: 'dropdown', source: ['', ...profileMapping.labels], width: 200 },
    // 11: TI Unit
    { data: 11, type: 'dropdown', source: tiUnitMap.labels, width: 120 },
    // 12: TI Growth Rate (applicable only to Fixed Amount / Amount per UoA)
    { data: 12, type: 'dropdown', source: ['', ...grMapping.labels], width: 140 },
    // 13: TI New (format depends on TI Unit)
    { data: 13, type: 'numeric', ...hotNumericFormat(2, true), width: 80 },
    // 14: TI Renew (format depends on TI Unit)
    { data: 14, type: 'numeric', ...hotNumericFormat(2, true), width: 80 },
    // 15: LC Unit
    { data: 15, type: 'dropdown', source: lcUnitMap.labels, width: 120 },
    // 16: LC Growth Rate (applicable only to Fixed Amount / Amount per UoA)
    { data: 16, type: 'dropdown', source: ['', ...grMapping.labels], width: 140 },
    // 17: LC New (format depends on LC Unit)
    { data: 17, type: 'numeric', ...hotNumericFormat(2, true), width: 80 },
    // 18: LC Renew (format depends on LC Unit)
    { data: 18, type: 'numeric', ...hotNumericFormat(2, true), width: 80 },
    // 19: MLA After Expiry (dynamic dropdown per row)
    { data: 19, type: 'dropdown', source: [], width: 130 }
  ], [grMapping.labels, marketRentUnitMap.labels, lcUnitMap.labels, tiUnitMap.labels, profileMapping.labels])

  // Measure viewport height and pass it to Handsontable so headers and body
  // share a single scroll container (prevents double horizontal scrollbar).
  useEffect(() => {
    const el = viewportRef.current
    if (!el) return

    const update = () => {
      const next = el.clientHeight
      setHotHeight(prev => (prev === next ? prev : next))
      hotRef.current?.hotInstance?.refreshDimensions?.()
    }

    update()

    let ro
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => update())
      ro.observe(el)
    } else {
      window.addEventListener('resize', update)
    }

    return () => {
      if (ro) ro.disconnect()
      else window.removeEventListener('resize', update)
    }
  }, [])

  const hotSettings = useMemo(() => ({
    data: tableData,
    columns,
    nestedHeaders,
    rowHeaders: false,
    colHeaders: true,
    fixedColumnsStart: 1,
    themeName: 'ht-theme-horizon',
    licenseKey: HOT_LICENSE_KEY,
    sanitizer: sanitizeHandsontableHtml,
    stretchH: 'none',
    width: '100%',
    height: hotHeight || undefined,
    manualColumnResize: true,
    contextMenu: readOnly ? false : {
      items: {
        delete_row_custom: {
          name: t('delete_mla', translations),
          callback(_key, selection) {
            const row = selection[0]?.start?.row
            if (row !== undefined) handleDeleteRow(row)
          }
        }
      }
    },
    cells(row, col) {
      const cellProps = {}
      if (readOnly) {
        cellProps.readOnly = true
        return cellProps
      }

      // Formula support for eligible numeric columns
      if (FORMULA_ELIGIBLE_COLS.has(col)) {
        cellProps.renderer = formulaAwareRenderer
        cellProps.editor = FormulaEditor
        cellProps.allowInvalid = true
      }

      // Renewal Probability: always show "%" suffix
      if (col === COL.RENEWAL_PROB) {
        cellProps.unitSuffix = '%'
        return cellProps
      }

      // LC New/Renew: format depends on LC Unit
      if (col === COL.LC_NEW || col === COL.LC_RENEW) {
        const fmt = getUnitDependentFormat(row, col)
        Object.assign(cellProps, fmt.hotFormat)
        if (fmt.suffix) cellProps.unitSuffix = fmt.suffix
        if (fmt.readOnly) cellProps.readOnly = true
        return cellProps
      }

      // TI New/Renew: format depends on TI Unit
      if (col === COL.TI_NEW || col === COL.TI_RENEW) {
        const fmt = getUnitDependentFormat(row, col)
        Object.assign(cellProps, fmt.hotFormat)
        if (fmt.suffix) cellProps.unitSuffix = fmt.suffix
        if (fmt.readOnly) cellProps.readOnly = true
        return cellProps
      }

      // TI/LC Growth Rate: only applicable to Fixed Amount / Amount per rentable
      // area. For every other unit the profile has no effect (event engines force
      // factor 1), so the cell is locked to avoid misleading input.
      if (col === COL.TI_GROWTH_RATE || col === COL.LC_GROWTH_RATE) {
        const record = rawData[row]
        const unit = col === COL.TI_GROWTH_RATE ? record?.ti_unit : record?.lc_unit
        const applicable = unit === 'CURRENCY_AMOUNT' || unit === 'CURRENCY_PER_UOA'
        if (!applicable) cellProps.readOnly = true
        return cellProps
      }

      // After Expiry: dropdown with all MLA names (including self — cycling is valid)
      if (col === COL.AFTER_EXPIRY) {
        const allNames = rawData
          .filter(r => r.name && r.name.trim() !== '')
          .map(r => r.name)
        cellProps.type = 'dropdown'
        cellProps.source = ['', ...allNames]
        return cellProps
      }

      return cellProps
    },
    beforePaste: handleBeforePaste,
    beforeOnCellMouseDown(event, coords) {
      if (readOnly) return
      const hot = hotRef.current?.hotInstance
      if (!hot) return
      const editor = hot.getActiveEditor()
      if (!editor || !editor.isOpened()) return

      const currentValue = editor.TEXTAREA?.value || ''
      if (!currentValue.startsWith('=')) return

      // Only insert reference for formula-eligible columns
      if (!FORMULA_ELIGIBLE_COLS.has(coords.col)) return

      const clickedRow = rawData[coords.row]
      if (!clickedRow) return

      // Prevent Handsontable from closing editor and selecting the clicked cell
      event.stopImmediatePropagation()

      const ref = `mla[${clickedRow.id}].${COL_PROPS[coords.col]}`
      const textarea = editor.TEXTAREA
      const pos = textarea.selectionStart
      const before = currentValue.slice(0, pos)
      const after = currentValue.slice(textarea.selectionEnd)
      textarea.value = before + ref + after
      textarea.selectionStart = textarea.selectionEnd = pos + ref.length
      textarea.focus()
      textarea.dispatchEvent(new Event('input', { bubbles: true }))
    },
    afterChange(changes, source) {
      if (!changes || source === SOURCE.LOAD || source === SOURCE.SERVER || readOnly) return

      changes.forEach(([row, col, oldVal, newVal]) => {
        if (typeof col !== 'number') return

        const recordId = rawData[row]?.id
        if (!recordId) return

        // EVA-294: Name is `presence: true` on the model, and the add-row
        // handler opens this editor empty on purpose so the valuer can type
        // straight away. Abandoning it commits "" — send that and the whole
        // batch rolls back, so the user gets "Failed to save changes" for a row
        // that saved fine. Revert instead, exactly as GrowthRatesTable and
        // AssumptionGrowthRatesTable already do for their title column.
        if (col === COL.NAME) {
          const trimmed = (newVal || '').trim()
          if (!trimmed) {
            hotRef.current?.hotInstance?.setDataAtCell(row, col, oldVal, SOURCE.SERVER)
            return
          }
          if (!pendingRef.current.has(recordId)) pendingRef.current.set(recordId, new Map())
          pendingRef.current.get(recordId).set(col, trimmed)
          return
        }

        const colProp = COL_PROPS[col]
        const isFormula = typeof newVal === 'string' && newVal.startsWith('=')
        const isFormulaEligible = FORMULA_ELIGIBLE_COLS.has(col)

        if (isFormula && isFormulaEligible) {
          if (!pendingRef.current.has(recordId)) pendingRef.current.set(recordId, new Map())
          pendingRef.current.get(recordId).set(col, { formula: newVal })
          // Track locally for instant indicator rendering
          _localFormulas.set(`${recordId}:${colProp}`, newVal)
          // Restore old display value while formula is being sent to backend
          const hot = hotRef.current?.hotInstance
          if (hot) {
            hot.setDataAtCell(row, col, oldVal, SOURCE.SERVER)
          }
        } else {
          // Check if this cell previously had a formula — clear it
          const rowFormulas = rawData[row]?.formulas
          const hadLocalFormula = _localFormulas.has(`${recordId}:${colProp}`)
          if (rowFormulas?.[colProp] || hadLocalFormula) {
            _localFormulas.delete(`${recordId}:${colProp}`)
            if (!pendingRef.current.has(recordId)) pendingRef.current.set(recordId, new Map())
            pendingRef.current.get(recordId).set(col, { value: displayToServer(col, newVal), clearFormula: colProp })
          } else {
            const serverValue = displayToServer(col, newVal)
            if (!pendingRef.current.has(recordId)) pendingRef.current.set(recordId, new Map())
            pendingRef.current.get(recordId).set(col, serverValue)
          }
        }
      })

      scheduledFlush()
    },
    afterDocumentKeyDown(event) {
      if (readOnly) return
      const hot = hotRef.current?.hotInstance
      handleHotKeyboardShortcut(event, {
        onAddRow: () => window.dispatchEvent(new CustomEvent('addMlaRow')),
        onDeleteRow: () => {
          if (!hot) return
          const selected = hot.getSelectedRangeLast()
          if (!selected) return
          handleDeleteRow(selected.from.row)
        }
      })
    }
  }), [tableData, columns, nestedHeaders, readOnly, rawData, handleDeleteRow, scheduledFlush, hotHeight, handleBeforePaste, translations])

  // Expose the Handsontable instance for end-to-end specs and console debugging
  // (mirrors window.__evaTenantsHot from EVA-153)
  useEffect(() => {
    if (typeof window !== 'undefined' && !loading) {
      window.__evaMlaHot = hotRef.current?.hotInstance
    }
    return () => {
      if (typeof window !== 'undefined') delete window.__evaMlaHot
    }
  })

  return (
    <div className="mla-table-container">
      {loading ? (
        <div className="d-flex justify-content-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('loading', translations)}</span>
          </div>
        </div>
      ) : (
        <div ref={viewportRef} className="mla-table-viewport">
          <HotTable ref={hotRef} {...hotSettings} />
        </div>
      )}

      <ConnectionStatus
        status={connectionStatus}
        mountEl={connectionMountEl}
        labels={{
          connected: t('connection.live', translations),
          disconnected: t('connection.disconnected', translations),
          connecting: t('connection.connecting', translations),
        }}
      />
    </div>
  )
}
